import React, { useEffect, useMemo, useState } from 'react';
import { collection, addDoc, doc, updateDoc } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { auth, db, storage } from '../firebase/firebase';
import { useSnackbar } from 'notistack';
import { motion } from 'framer-motion';
import { FiX } from 'react-icons/fi';
import MapPicker from './MapPicker';
import 'bootstrap-icons/font/bootstrap-icons.css';
import './ModelForm.css';

/** Mapea etiquetas visibles -> códigos internos del Dashboard */
const CATEGORY_MAP = {
  'Artefacto': 'artifact',
  'Escultura': 'sculpture',
  'Herramienta': 'tool',
  'Arma': 'weapon',
  'Joyería': 'jewelry',
  'Otro': 'other'
};
const CATEGORY_ICON = {
  artifact: 'bi-box-seam',
  sculpture: 'bi-person-bounding-box',
  tool: 'bi-tools',
  weapon: 'bi-shield',
  jewelry: 'bi-gem',
  other: 'bi-question-circle'
};

const CATEGORY_OPTIONS = [
  { value: 'artifact',   label: 'Artefacto' },
  { value: 'sculpture',  label: 'Escultura' },
  { value: 'tool',       label: 'Herramienta' },
  { value: 'weapon',     label: 'Arma' },
  { value: 'jewelry',    label: 'Joyería' },
  { value: 'other',      label: 'Otro' },
];

const ERA_OPTIONS = [
  { value: 'Prehistoria',         label: 'Prehistoria',         icon: 'bi-moon' },
  { value: 'Antigüedad',          label: 'Antigüedad',          icon: 'bi-columns' },
  { value: 'Edad Media',          label: 'Edad Media',          icon: 'bi-castle' },
  { value: 'Renacimiento',        label: 'Renacimiento',        icon: 'bi-palette' },
  { value: 'Edad Moderna',        label: 'Edad Moderna',        icon: 'bi-clock-history' },
  { value: 'Edad Contemporánea',  label: 'Edad Contemporánea',  icon: 'bi-calendar3' },
];
const MAX_MODEL_SIZE_MB = 500;
const MAX_THUMBNAIL_SIZE_MB = 5;

const normalizeCategory = (c) => {
  if (!c) return 'other';
  // Si ya viene código interno, lo dejamos
  if (CATEGORY_ICON[c]) return c;
  // Si viene con etiqueta visible, la mapeamos
  return CATEGORY_MAP[c] || 'other';
};

const ModelForm = ({ model, onClose, editMode, refreshModels }) => {
  const { enqueueSnackbar } = useSnackbar();

  const [formData, setFormData] = useState({
    name:        model?.name || '',
    artist:      model?.artist || '',
    description: model?.description || '',
    category:    normalizeCategory(model?.category) || 'other',
    era:         model?.era || '',
    location:    model?.location || '',
    coordinates: model?.coordinates || null,
  });

  const [modelFile, setModelFile] = useState(null);
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [thumbnailPreview, setThumbnailPreview] = useState(model?.thumbnailUrl || null);

  const [isLoading, setIsLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [errors, setErrors] = useState({});
  const [showMap, setShowMap] = useState(false);
  const [dragOver, setDragOver] = useState({ model: false, thumbnail: false });

  useEffect(() => {
    if (!thumbnailFile) return;
    const reader = new FileReader();
    reader.onloadend = () => setThumbnailPreview(reader.result);
    reader.readAsDataURL(thumbnailFile);
    return () => reader.abort();
  }, [thumbnailFile]);

  const currentCategoryIcon = useMemo(
    () => CATEGORY_ICON[formData.category] || CATEGORY_ICON.other,
    [formData.category]
  );
  const currentEraIcon = useMemo(
    () => (ERA_OPTIONS.find(e => e.value === formData.era)?.icon || 'bi-clock'),
    [formData.era]
  );

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => {
        const cp = { ...prev };
        delete cp[name];
        return cp;
      });
    }
  };

  const handleMapSelect = (location, coordinates) => {
    setFormData(prev => ({ ...prev, location, coordinates }));
    setShowMap(false);
  };

  const clearError = (key) => {
    if (!errors[key]) return;
    setErrors(prev => {
      const cp = { ...prev };
      delete cp[key];
      return cp;
    });
  };

  const handleModelFileSelected = (file) => {
    setModelFile(file || null);
    clearError('modelFile');
  };

  const handleThumbnailFileSelected = (file) => {
    setThumbnailFile(file || null);
    clearError('thumbnailFile');
  };

  const formatBytes = (bytes) => {
    if (!bytes && bytes !== 0) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim())        newErrors.name = 'Nombre es requerido';
    if (!formData.category)           newErrors.category = 'Categoría es requerida';
    if (!formData.era)                newErrors.era = 'Época es requerida';
    if (!formData.location.trim())    newErrors.location = 'Ubicación es requerida';
    if (!formData.description.trim()) newErrors.description = 'Descripción es requerida';

    if (!editMode && !modelFile) newErrors.modelFile = 'Archivo 3D es requerido';

    if (modelFile) {
      const valid = ['.glb', '.gltf'];
      const ext = modelFile.name.substring(modelFile.name.lastIndexOf('.')).toLowerCase();
      if (!valid.includes(ext)) {
        newErrors.modelFile = 'Solo se permiten archivos .glb o .gltf';
      }

      const maxSizeBytes = MAX_MODEL_SIZE_MB * 1024 * 1024;
      if (modelFile.size > maxSizeBytes) {
        newErrors.modelFile = `El archivo no debe exceder ${MAX_MODEL_SIZE_MB}MB`;
      }
    }

    if (thumbnailFile && thumbnailFile.size > MAX_THUMBNAIL_SIZE_MB * 1024 * 1024) {
      newErrors.thumbnailFile = `La miniatura no debe exceder ${MAX_THUMBNAIL_SIZE_MB}MB`;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /** Subida con progreso real */
  const uploadFile = (file, path) =>
    new Promise((resolve, reject) => {
      const storageRef = ref(storage, `${path}/${Date.now()}_${file.name}`);
      const task = uploadBytesResumable(storageRef, file);
      task.on(
        'state_changed',
        (snap) => {
          const pct = Math.round((snap.bytesTransferred / snap.totalBytes) * 100);
          setUploadProgress(pct);
        },
        (err) => reject(err),
        async () => {
          const url = await getDownloadURL(storageRef);
          resolve(url);
        }
      );
    });

  /** Limpia undefined (Firestore no lo acepta) */
  const cleanForFirestore = (obj) =>
    Object.fromEntries(
      Object.entries(obj).map(([k, v]) => [k, v === undefined ? null : v])
    );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isLoading) return;

    if (!validateForm()) return;

    setIsLoading(true);
    setUploadProgress(0);

    try {
      let modelUrl = model?.modelUrl ?? null;
      let thumbnailUrl = model?.thumbnailUrl ?? null;

      if (modelFile)     modelUrl = await uploadFile(modelFile, 'models');
      if (thumbnailFile) thumbnailUrl = await uploadFile(thumbnailFile, 'thumbnails');

      const nowISO = new Date().toISOString();

      const payload = cleanForFirestore({
        name:        formData.name.trim(),
        artist:      formData.artist.trim(),
        description: formData.description.trim(),
        category:    formData.category || 'other', // código interno
        era:         formData.era || '',
        location:    formData.location.trim(),
        coordinates: formData.coordinates || null,

        modelUrl:    modelUrl ?? null,
        thumbnailUrl: thumbnailUrl ?? null,

        userId:      auth.currentUser?.uid || 'anonymous',
        isPublic:    typeof model?.isPublic === 'boolean' ? model.isPublic : false,

        createdAt:   model?.createdAt || nowISO,
        lastUpdated: nowISO
      });

      if (model?.id) {
        await updateDoc(doc(db, 'models', model.id), payload);
        enqueueSnackbar('Modelo actualizado exitosamente', { variant: 'success' });
      } else {
        await addDoc(collection(db, 'models'), payload);
        enqueueSnackbar('Modelo creado exitosamente', { variant: 'success' });
      }

      await refreshModels?.();
      onClose?.();
    } catch (err) {
      console.error('Error saving model:', err);
      enqueueSnackbar(`Error: ${err.message}`, { variant: 'error' });
    } finally {
      setIsLoading(false);
      setUploadProgress(0);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="model-form-container fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
    >
      <motion.div
        initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: -20, opacity: 0 }}
        className="model-form-content bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col"
      >
        {/* Header */}
        <div className="sticky top-0 bg-white dark:bg-gray-800 p-6 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              {model?.id ? 'Editar Modelo 3D' : 'Nuevo Modelo 3D'}
            </h2>
            <p className="model-form-subtitle">
              Completa los datos curatorios y sube los archivos del modelo.
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition-colors p-1 rounded-full"
            disabled={isLoading}
            aria-label="Cerrar"
          >
            <FiX size={24} />
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 p-6 model-form-transition">
          {Object.keys(errors).length > 0 && (
            <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500 rounded error-message">
              <h3 className="font-medium text-red-700 dark:text-red-300 mb-2">
                Por favor corrige los siguientes errores:
              </h3>
              <ul className="list-disc pl-5 text-red-600 dark:text-red-300">
                {Object.values(errors).map((error, i) => <li key={i}>{error}</li>)}
              </ul>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Básico */}
            <section className="model-form-section">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <i className="bi bi-info-circle text-blue-600"></i>
                Información Básica
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 model-form-grid">
                {/* Nombre */}
                <div>
                  <label className="block text-sm font-medium text-gray-800 dark:text-gray-200 mb-1">
                    Nombre del modelo *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className={`model-form-input w-full px-4 py-2 rounded-lg border ${errors.name ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'} focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-gray-100`}
                    placeholder="Ej: Vasija griega del siglo V"
                  />
                </div>

                {/* Artista / Escultor */}
                <div>
                  <label className="block text-sm font-medium text-gray-800 dark:text-gray-200 mb-1 flex items-center gap-1">
                    <i className="bi bi-person-badge"></i> Artista / Escultor
                  </label>
                  <input
                    type="text"
                    name="artist"
                    value={formData.artist}
                    onChange={handleChange}
                    className="model-form-input w-full px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-gray-100"
                    placeholder="Ej: María Gómez"
                  />
                  <p className="help-text mt-2">Se muestra en la web pública para que los visitantes puedan filtrar por artista.</p>
                </div>

                {/* Categoría */}
                <div>
                  <label className="block text-sm font-medium text-gray-800 dark:text-gray-200 mb-1">
                    Categoría *
                  </label>
                  <div className="flex items-center">
                    <i className={`bi ${currentCategoryIcon} mr-2 text-lg text-gray-700 dark:text-gray-300`}></i>
                    <select
                      name="category"
                      value={formData.category}
                      onChange={handleChange}
                      className={`model-form-input w-full px-4 py-2 rounded-lg border ${errors.category ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'} focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-gray-100`}
                    >
                      <option value="">Selecciona una categoría</option>
                      {CATEGORY_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Época */}
                <div>
                  <label className="block text-sm font-medium text-gray-800 dark:text-gray-200 mb-1 flex items-center gap-1">
                    <i className="bi bi-clock-history"></i> Época histórica *
                  </label>
                  <div className="flex items-center">
                    <i className={`bi ${currentEraIcon} mr-2 text-lg text-gray-700 dark:text-gray-300`}></i>
                    <select
                      name="era"
                      value={formData.era}
                      onChange={handleChange}
                      className={`model-form-input w-full px-4 py-2 rounded-lg border ${errors.era ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'} focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-gray-100`}
                    >
                      <option value="">Selecciona una época</option>
                      {ERA_OPTIONS.map(opt => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Ubicación */}
                <div>
                  <label className="block text-sm font-medium text-gray-800 dark:text-gray-200 mb-1 flex items-center gap-1">
                    <i className="bi bi-geo-alt"></i> Ubicación *
                  </label>
                  <div className="flex gap-2 model-form-file-container">
                    <input
                      type="text"
                      name="location"
                      value={formData.location}
                      onChange={handleChange}
                      className={`model-form-input flex-1 px-4 py-2 rounded-lg border ${errors.location ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'} focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-gray-100`}
                      placeholder="Ej: Museo del Louvre, París"
                    />
                    <button
                      type="button"
                      onClick={() => setShowMap(true)}
                      className="model-form-map-button px-3 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg text-gray-700 dark:text-gray-300 flex items-center gap-1"
                      title="Seleccionar en mapa"
                    >
                      <i className="bi bi-map"></i>
                    </button>
                  </div>
                  {formData.coordinates?.lat && formData.coordinates?.lng && (
                    <p className="help-text mt-2">
                      Coordenadas: {Number(formData.coordinates.lat).toFixed(5)}, {Number(formData.coordinates.lng).toFixed(5)}
                    </p>
                  )}
                </div>
              </div>
            </section>

            {/* Descripción */}
            <section className="model-form-section">
              <label className="block text-sm font-medium text-gray-800 dark:text-gray-200 mb-1">
                Descripción detallada *
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={4}
                className={`model-form-input w-full px-4 py-2 rounded-lg border ${errors.description ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'} focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-gray-100`}
                placeholder="Describe el modelo, su importancia histórica, materiales, etc."
              />
            </section>

            {/* Archivos */}
            <section className="model-form-section">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <i className="bi bi-upload text-blue-600"></i>
                Archivos del Modelo
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 model-upload-grid">
                {/* Modelo 3D */}
                <div className="upload-card">
                  <label className="block text-sm font-medium text-gray-800 dark:text-gray-200 mb-1">
                    Archivo 3D (GLB/GLTF) {!editMode && '*'}
                  </label>

                  <label
                    className={`file-upload-area flex flex-col items-center justify-center p-6 rounded-xl border-2 border-dashed ${errors.modelFile ? 'border-red-500 bg-red-50 dark:bg-red-900/10' : 'border-gray-300 dark:border-gray-600'} ${dragOver.model ? 'drag-over' : ''} transition-colors cursor-pointer`}
                    data-type="model"
                    onDragOver={(e) => { e.preventDefault(); setDragOver(prev => ({ ...prev, model: true })); }}
                    onDragLeave={() => setDragOver(prev => ({ ...prev, model: false }))}
                    onDrop={(e) => {
                      e.preventDefault();
                      setDragOver(prev => ({ ...prev, model: false }));
                      handleModelFileSelected(e.dataTransfer.files?.[0] || null);
                    }}
                  >
                    <i className="bi bi-file-earmark-3d text-4xl text-gray-500 dark:text-gray-400 mb-2"></i>
                    <p className="text-sm text-gray-700 dark:text-gray-300 text-center">
                      {modelFile
                        ? <span className="font-medium text-blue-600 dark:text-blue-400">{modelFile.name}</span>
                        : model?.modelUrl
                          ? 'Haz clic para cambiar el modelo'
                          : 'Arrastra o haz clic para subir'}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Formatos: .glb, .gltf · Máx. {MAX_MODEL_SIZE_MB}MB
                    </p>
                    <input
                      type="file"
                      accept=".glb,.gltf"
                      onChange={(e) => handleModelFileSelected(e.target.files?.[0] || null)}
                      className="hidden"
                    />
                  </label>

                  {modelFile && (
                    <div className="upload-file-pill">
                      <span className="upload-file-main">
                        <i className="bi bi-file-earmark-3d" />
                        {modelFile.name}
                      </span>
                      <span className="upload-file-size">{formatBytes(modelFile.size)}</span>
                      <button
                        type="button"
                        onClick={() => handleModelFileSelected(null)}
                        className="upload-pill-remove"
                        aria-label="Quitar archivo 3D"
                      >
                        <i className="bi bi-x-lg" />
                      </button>
                    </div>
                  )}

                  {model?.modelUrl && (
                    <p className="mt-2 text-xs text-gray-600 dark:text-gray-400 truncate">
                      Modelo actual: {model.modelUrl.split('/').pop()}
                    </p>
                  )}
                </div>

                {/* Miniatura */}
                <div className="upload-card">
                  <label className="block text-sm font-medium text-gray-800 dark:text-gray-200 mb-1">
                    Miniatura (Opcional)
                  </label>

                  <div className="flex items-center gap-4 model-form-file-container">
                    {thumbnailPreview && (
                      <div className="model-form-thumbnail-preview relative w-24 h-24 flex-shrink-0">
                        <img
                          src={thumbnailPreview}
                          alt="Vista previa"
                          className="w-full h-full object-cover rounded-lg border border-gray-200 dark:border-gray-600"
                        />
                      </div>
                    )}

                    <label
                      className={`file-upload-area flex-1 flex flex-col items-center justify-center p-4 rounded-xl border-2 border-dashed ${errors.thumbnailFile ? 'border-red-500 bg-red-50 dark:bg-red-900/10' : 'border-gray-300 dark:border-gray-600'} ${dragOver.thumbnail ? 'drag-over' : ''} transition-colors cursor-pointer`}
                      data-type="image"
                      onDragOver={(e) => { e.preventDefault(); setDragOver(prev => ({ ...prev, thumbnail: true })); }}
                      onDragLeave={() => setDragOver(prev => ({ ...prev, thumbnail: false }))}
                      onDrop={(e) => {
                        e.preventDefault();
                        setDragOver(prev => ({ ...prev, thumbnail: false }));
                        handleThumbnailFileSelected(e.dataTransfer.files?.[0] || null);
                      }}
                    >
                      <i className="bi bi-image text-3xl text-gray-500 dark:text-gray-400 mb-2"></i>
                      <p className="text-sm text-gray-700 dark:text-gray-300 text-center">
                        {thumbnailFile
                          ? <span className="font-medium text-blue-600 dark:text-blue-400">{thumbnailFile.name}</span>
                          : model?.thumbnailUrl
                            ? 'Haz clic para cambiar la miniatura'
                            : 'Arrastra o haz clic para subir'}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        Formatos: JPG/PNG · Máx. {MAX_THUMBNAIL_SIZE_MB}MB
                      </p>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleThumbnailFileSelected(e.target.files?.[0] || null)}
                        className="hidden"
                      />
                    </label>
                  </div>

                  {thumbnailFile && (
                    <div className="upload-file-pill mt-3">
                      <span className="upload-file-main">
                        <i className="bi bi-image" />
                        {thumbnailFile.name}
                      </span>
                      <span className="upload-file-size">{formatBytes(thumbnailFile.size)}</span>
                      <button
                        type="button"
                        onClick={() => handleThumbnailFileSelected(null)}
                        className="upload-pill-remove"
                        aria-label="Quitar miniatura"
                      >
                        <i className="bi bi-x-lg" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </section>

            {/* Progreso */}
            {isLoading && (
              <div className="space-y-2 model-upload-progress-wrap">
                <div className="flex justify-between text-sm text-gray-700 dark:text-gray-300 model-upload-progress-head">
                  <span>Subiendo archivos…</span>
                  <span>{uploadProgress}%</span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2.5 model-upload-progress-track">
                  <div
                    className="upload-progress bg-blue-600 h-2.5 rounded-full transition-all duration-300"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
              </div>
            )}

            {/* Acciones */}
            <div className="flex justify-end gap-3 pt-6 border-t border-gray-200 dark:border-gray-700 model-form-actions">
              <div className="model-form-actions-meta">
                <i className="bi bi-shield-check" />
                Los cambios se guardan de forma segura en la nube.
              </div>
              <button
                type="button"
                onClick={onClose}
                className="model-action-btn model-action-cancel"
                disabled={isLoading}
              >
                <i className="bi bi-x-circle"></i>
                Cancelar
              </button>
              <button
                type="submit"
                className="model-action-btn model-action-primary"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <i className="bi bi-arrow-repeat model-action-spin"></i>
                    {model?.id ? 'Guardando…' : 'Creando…'}
                  </>
                ) : (
                  <>
                    <i className="bi bi-save"></i>
                    {model?.id ? 'Guardar cambios' : 'Crear modelo 3D'}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </motion.div>

      {/* Modal mapa */}
      {showMap && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        >
          <motion.div
            initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: -20, opacity: 0 }}
            className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-4xl h-[80vh] flex flex-col"
          >
            <div className="sticky top-0 bg-white dark:bg-gray-800 p-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                <i className="bi bi-map me-2"></i>
                Seleccionar ubicación en el mapa
              </h2>
              <button
                onClick={() => setShowMap(false)}
                className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 transition-colors p-1 rounded-full"
              >
                <FiX size={24} />
              </button>
            </div>
            <div className="flex-1">
              <MapPicker
                initialLocation={formData.location}
                initialCoordinates={formData.coordinates}
                onSelect={handleMapSelect}
              />
            </div>
          </motion.div>
        </motion.div>
      )}
    </motion.div>
  );
};

export default ModelForm;
